import React, { useEffect, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { copyDelegableBranch, inheritBranch, retiredProduct, setBranch } from "./itemAccessTree";

const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
const PAGE_SIZE = 50;

function ProductPage({ businessUnitId, filter, value, words, renderItem }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const params = { business_unit_id: businessUnitId, [filter]: value, limit: PAGE_SIZE, offset };
    setStatus("loading");
    axios.get("/api/purchasing/user-item-access-rules/catalog-items", { params, signal: controller.signal }).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data?.items) || !Number.isInteger(data.total)) throw new Error("Invalid catalog page");
      setItems((previous) => offset === 0 ? data.items : [...previous.slice(0, offset), ...data.items]);
      setTotal(data.total); setStatus("ready");
    }).catch(() => { if (active) setStatus("error"); });
    return () => { active = false; controller.abort(); };
  }, [businessUnitId, filter, value, offset, attempt]);
  return <>
    {items.map(renderItem)}
    {status === "loading" && <p role="status">{words.loadingItems}</p>}
    {status === "error" && <p role="alert">{words.itemLoadError} <button type="button" onClick={() => setAttempt(attempt + 1)}>{words.retry}</button></p>}
    {status === "ready" && items.length < total && <button className="pu-tree-more" type="button" onClick={() => setOffset(items.length)}>{words.moreItems} ({items.length} / {total})</button>}
    {status === "ready" && !items.length && filter === "q" && <p>{words.noProducts}</p>}
  </>;
}

export default function ItemAccessTreeEditor({ tree, ceiling, limits, rules, summary, businessUnitId, ready, disabled, words, onChange }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 220);
    return () => clearTimeout(timer);
  }, [query]);
  const retired = [...tree.nodes.values()].filter(retiredProduct);
  const toggle = (key) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const setRoot = (allow) => onChange([{ policy_version: 2, rule_type: allow ? "allow" : "deny" }]);
  function renderNode(node, result = false) {
    const { key, kind, record } = node;
    const state = tree.getState(node);
    const isProduct = kind === "item";
    const within = limits.restricted && limits.canAdd(node);
    const blocked = limits.restricted && !within && !state.allow && !state.mixed;
    const toggleNode = () => {
      if (limits.restricted && within) onChange(copyDelegableBranch(rules, tree, ceiling, node));
      else onChange(setBranch(rules, tree, node, !limits.restricted && (state.mixed || !state.allow)));
    };
    const isOpen = expanded.has(key);
    const name = record.name || "#" + record.id;
    const origin = state.source === key ? words.explicit : state.source === "legacy" ? words.priorBase :
      state.source === "root" ? words.catalogBase : words.inheritedFrom + " " + tree.nodes.get(state.source)?.record.name;
    const path = node.path.slice(0, -1).map((id) => tree.nodes.get(id)?.record.name).filter(Boolean).join(" › ");
    const inspectBranch = () => {
      if (!result) { toggle(key); return; }
      setQuery(""); setSearch(""); setExpanded(new Set(node.path));
    };
    return <div className="pu-tree-node" key={key}>
      <div className="pu-tree-row">
        {!isProduct ? <button type="button" className="pu-tree-chevron" aria-label={name} aria-expanded={!result && isOpen} onClick={inspectBranch}>
          {isOpen && !result ? <FiChevronDown /> : <FiChevronRight />}</button> : <span className="pu-tree-spacer" />}
        <label><input type="checkbox" aria-label={name} checked={state.allow} aria-checked={state.mixed ? "mixed" : state.allow}
          ref={(input) => { if (input) input.indeterminate = Boolean(state.mixed); }} disabled={disabled || blocked}
          onChange={toggleNode} />
          <span>{name}{isProduct && record.code && <small> · {record.code}</small>}
            {result && path && <small className="pu-tree-path">{path}</small>}
          </span></label>
        <small className="pu-tree-origin">{blocked ? words.cannotGrant : <>{state.mixed ? words.partial + " · " : ""}{state.allow ? words.included : words.excluded} · {origin}</>}</small>
        {tree.specific.has(key) && <button type="button" className="pu-tree-inherit" disabled={disabled || limits.blockedInheritance.has(key)}
          aria-label={words.inherit + " · " + name} onClick={() => onChange(inheritBranch(rules, node))}>{words.inherit}</button>}
      </div>
      {!result && isOpen && !isProduct && <div className="pu-tree-children">
        {(tree.children.get(key) || []).filter((child) => child.kind !== "item").map((child) => renderNode(child))}
        {(kind === "category" || kind === "subcategory") && <ProductPage businessUnitId={businessUnitId} filter={kind + "_id"} value={record.id} words={words}
          renderItem={(item) => renderNode(tree.itemNode(item))} />}
      </div>}
    </div>;
  }
  const trimmedQuery = query.trim();
  return <div className="pu-tree">
    {ready && limits.restricted && <p>{words.delegationHint}</p>}
    {ready && <div className="pu-tree-summary">
      <strong>{summary.base}</strong>
      {tree.legacyBase && <small>{words.legacyHint}</small>}
      {summary.groups.map((group) => group.names.length > 3 ? <details key={group.key}><summary>
        <b>{group.title}</b> · {group.names.slice(0, 3).join(", ")} …</summary><span>{group.names.join(", ")}</span></details>
        : <div key={group.key}><b>{group.title}</b><span>{group.names.join(", ")}</span></div>)}
      {retired.length > 0 && <details className="pu-tree-retired"><summary>{words.retiredItems} ({retired.length})</summary>
        {retired.map((node) => <div key={node.key}>
          <span>{node.record.name} · {tree.specific.get(node.key) === "allow" ? words.included : words.excluded}</span>
          <button type="button" disabled={disabled || limits.blockedInheritance.has(node.key)} aria-label={words.inherit + " · " + node.record.name}
            onClick={() => onChange(inheritBranch(rules, node))}>{words.inherit}</button>
        </div>)}
      </details>}
    </div>}
    <button type="button" className="pu-tree-edit" disabled={!ready} aria-expanded={open} onClick={() => setOpen(!open)}>
      {open ? <FiChevronDown /> : <FiChevronRight />}{open ? words.closeTree : words.editTree}</button>
    {open && ready && <div className="pu-tree-panel">
      <p>{limits.restricted ? words.limitedTreeHint : words.treeHint}</p>
      <div className="pu-tree-base"><span>{words.startFrom}</span>
        <button type="button" disabled={disabled} onClick={() => limits.restricted
          ? onChange(copyDelegableBranch(rules, tree, ceiling)) : setRoot(true)}>{limits.restricted ? words.useMine : words.selectAll}</button>
        <button type="button" disabled={disabled} onClick={() => setRoot(false)}>{words.clearAll}</button></div>
      <label className="pu-tree-search">{words.findNodes}<input type="search" maxLength={255} value={query} onChange={(event) => setQuery(event.target.value)} /></label>
      <div role="group" aria-label={words.items} className="pu-tree-scroll">
        {trimmedQuery ? <>
          {[...tree.nodes.values()].filter((node) => node.kind !== "item" && normalize([node.record.name, node.record.code].join(" ")).includes(normalize(trimmedQuery))).map((node) => renderNode(node, true))}
          {search === trimmedQuery ? <ProductPage key={search} businessUnitId={businessUnitId} filter="q" value={search} words={words}
            renderItem={(item) => renderNode(tree.itemNode(item), true)} /> : <p role="status">{words.loadingItems}</p>}
        </> : (tree.children.get("root") || []).map((node) => renderNode(node))}
      </div>
    </div>}
  </div>;
}
